/**
 * ClosureReasonSelect
 *
 * Async-paginated dropdown for case / lead closure reasons.
 * Mirrors PracticeAreaSelectV2 / CaseStageSelect — uses list-closure-reasons-v2.
 *
 * @param {Object|Array|null} props.value          - Selected option(s) or null
 * @param {Function}     props.onChange           - (option | option[] | null, actionMeta) => void
 * @param {boolean}      [props.isMulti]          - Multi-select (e.g. report filters)
 * @param {boolean}      [props.is_lead=false]    - Case: false, Lead: true
 * @param {string}       [props.placeholder]
 * @param {boolean}      [props.isClearable]
 * @param {boolean}      [props.isDisabled]
 * @param {string}       [props.className]
 * @param {number}       [props.pageSize]
 * @param {string}       [props.sort_by]
 * @param {string}       [props.sort_order]
 * @param {string}       [props.practice_area_id]  - Filters reasons to case practice area (dashboard)
 * @param {Element|null} [props.menuPortalTarget]
 * @param {string}       [props.menuPosition]
 */

import React, { useMemo, useState } from 'react';
import { AsyncPaginate } from 'react-select-async-paginate';
import { Tooltip } from 'primereact/tooltip';
import { useQueryClient } from '@tanstack/react-query';
import { fetchClosureReasonsPage } from 'hooks/queries/useClosureReasonsListV2Query';
import { queryKeys } from 'hooks/config/queryClient';
import {
  getSummarizedMultiSelectClassNames,
  mergeSummarizedMultiSelectStyles,
  withSummarizedMultiValue,
} from '../shared/createSummarizedMultiValue';
import '../shared/AsyncSelect.scss';
import '../PracticeAreaSelect/PracticeAreaSelectV2.scss';
import './ClosureReasonSelect.scss';

const getLabel = (option) => option?.closure_reason || option?.label || '';
const getValue = (option) => option?.closure_reason || option?.value || '';

function OptionLabel({ tooltipText, children, data }) {
  const safeTooltipClass = `closure-reason-option-tooltip-${String(getValue(data) || tooltipText || 'option')
    .replace(/[^a-zA-Z0-9_-]/g, '_')
    .slice(0, 80)}`;
  return (
    <>
      <span
        className={`pa-select-v2-option__label ${safeTooltipClass}`}
        style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
      >
        {children}
      </span>
      <Tooltip target={`.${safeTooltipClass}`} content={tooltipText} position="top" />
    </>
  );
}

function OptionWithTooltip({ innerRef, innerProps, isFocused, isSelected, isDisabled, children, data }) {
  const tooltipText = getLabel(data) || (typeof children === 'string' ? children : '');
  return (
    <div
      ref={innerRef}
      {...innerProps}
      className={[
        'async-select-custom__option',
        isFocused ? 'async-select-custom__option--is-focused' : '',
        isSelected ? 'async-select-custom__option--is-selected' : '',
        isDisabled ? 'async-select-custom__option--is-disabled' : '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <OptionLabel tooltipText={tooltipText} data={data}>
        {children}
      </OptionLabel>
    </div>
  );
}

function CheckboxOption({ innerRef, innerProps, isFocused, isSelected, isDisabled, children, data }) {
  const tooltipText = getLabel(data) || (typeof children === 'string' ? children : '');
  return (
    <div
      ref={innerRef}
      {...innerProps}
      className={[
        'async-select-custom__option',
        isFocused ? 'async-select-custom__option--is-focused' : '',
        isSelected ? 'async-select-custom__option--is-selected' : '',
        isDisabled ? 'async-select-custom__option--is-disabled' : '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <div className={`pa-select-v2-option${isDisabled ? ' pa-select-v2-option--disabled' : ''}`}>
        <span className={`pa-select-v2-option__checkbox${isSelected ? ' pa-select-v2-option__checkbox--checked' : ''}`}>
          {isSelected && (
            <svg className="pa-select-v2-option__checkbox__tick" viewBox="0 0 10 10">
              <polyline points="1.5,5 4,7.5 8.5,2.5" />
            </svg>
          )}
        </span>
        <OptionLabel tooltipText={tooltipText} data={data}>
          {children}
        </OptionLabel>
      </div>
    </div>
  );
}

function ClosureReasonSelect({
  value = null,
  onChange,
  isMulti = false,
  is_lead = false,
  placeholder = 'Select a Reason',
  isClearable = true,
  isDisabled = false,
  className = '',
  pageSize = 20,
  sort_by = 'closure_reason',
  sort_order = 'asc',
  practice_area_id = '',
  menuPortalTarget = null,
  menuPosition = 'absolute',
  styles = {},
  name,
  id,
  onBlur,
  onFocus,
  ...rest
}) {
  const queryClient = useQueryClient();
  const [inputValue, setInputValue] = useState('');

  const loadOptions = useMemo(
    () => async (searchInput, _loadedOptions, additional) => {
      const page = (additional?.page ?? 0) + 1;
      const keyword = searchInput?.trim() ?? '';

      const result = await queryClient.fetchQuery({
        queryKey: queryKeys.closureReasonsV2.dropdown(is_lead, sort_by, sort_order, keyword, page, pageSize, practice_area_id),
        queryFn: () =>
          fetchClosureReasonsPage({
            is_lead,
            page,
            pageSize,
            sort_by,
            sort_order,
            keyword,
            practice_area_id,
            excludeFromLoading: true,
          }),
        staleTime: 5 * 60 * 1000,
        gcTime: 10 * 60 * 1000,
      });

      return {
        options: result.options,
        hasMore: result.hasMore,
        additional: result.additional,
      };
    },
    [queryClient, is_lead, sort_by, sort_order, pageSize, practice_area_id]
  );

  const mergedStyles = useMemo(
    () =>
      mergeSummarizedMultiSelectStyles(isMulti, {
        menu: (base) => ({ ...base, zIndex: 9999 }),
        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
        ...styles,
      }),
    [isMulti, styles]
  );

  const components = useMemo(() => withSummarizedMultiValue(isMulti, { Option: isMulti ? CheckboxOption : OptionWithTooltip }), [isMulti]);

  const summarizedClassNames = useMemo(() => getSummarizedMultiSelectClassNames(isMulti), [isMulti]);

  const handleChange = (option, actionMeta) => {
    if (actionMeta?.action === 'select-option' || actionMeta?.action === 'clear') {
      setInputValue('');
    }
    onChange(option, actionMeta);
  };

  const handleInputChange = (value, { action }) => {
    if (action === 'input-change') {
      setInputValue(value);
      return;
    }
    if (action === 'set-value' || action === 'clear' || action === 'menu-close') {
      setInputValue('');
    }
  };

  return (
    <>
      <AsyncPaginate
        value={value}
        loadOptions={loadOptions}
        onChange={handleChange}
        getOptionLabel={getLabel}
        getOptionValue={getValue}
        isMulti={isMulti}
        closeMenuOnSelect={!isMulti}
        hideSelectedOptions={false}
        placeholder={placeholder}
        isClearable={isClearable}
        isDisabled={isDisabled}
        isSearchable
        components={components}
        className={`closure-reason-select ${className}`.trim()}
        classNamePrefix="async-select-custom"
        classNames={summarizedClassNames}
        styles={mergedStyles}
        inputValue={inputValue}
        onInputChange={handleInputChange}
        cacheUniqs={[is_lead, sort_by, sort_order, practice_area_id]}
        additional={{ page: 0 }}
        debounceTimeout={300}
        loadingMessage={() => 'Loading...'}
        noOptionsMessage={({ inputValue }) => (inputValue ? `No reasons found for "${inputValue}"` : 'No reasons found')}
        menuShouldScrollIntoView={false}
        closeMenuOnScroll={(e) => e.target === document}
        menuPortalTarget={menuPortalTarget}
        menuPosition={menuPosition}
        name={name}
        id={id}
        onBlur={onBlur}
        onFocus={onFocus}
        {...rest}
      />
    </>
  );
}

export default ClosureReasonSelect;
